import React, { memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, Trash2, Plus, Copy, TrendingUp, TrendingDown } from 'lucide-react';

import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';

import { formatDuration } from '../../lib/exerciseDisplay';
import { TA } from '../tour/tourAnchors';

// Shared set-type catalogue + popover. v3.8.178 moved these out of
// LiveSession.js into sharedSetTypes.js to break the
// SetRow → LiveSession → ExerciseRow → SetRow import cycle.
import { SET_TYPES, SetTypePopover } from './sharedSetTypes';
import { cardioGridClass } from './cardioGridLayouts';

/* ════════════════════════════════════════════════════════════════
   <SwipeableSet>

   Pointer-driven swipe wrapper: drags reveal a delete (left) or
   complete (right) action. Lifted from LiveSession.js verbatim
   into SetRow so the per-row state lives where it's used. Nothing
   that crosses memo boundaries lives here — the swipe is purely
   local DOM manipulation, so it never causes parent re-renders.
   ════════════════════════════════════════════════════════════════ */
function SwipeableSet({ children, onDelete, onComplete, isCompleted }) {
  const ref = useRef(null);
  const startX = useRef(0);
  const currentX = useRef(0);
  const dragging = useRef(false);
  const actionQueued = useRef(false);
  const direction = useRef(null); // 'left' | 'right' | null
  const threshold = 80;

  const isInteractiveTarget = (el) => {
    const tag = el.tagName;
    if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return true;
    if (el.closest('button, input, select, textarea, [role="button"], [data-radix-collection-item]')) return true;
    return false;
  };

  const handleStart = (e) => {
    if (isInteractiveTarget(e.target)) {
      dragging.current = false;
      return;
    }
    startX.current = e.clientX;
    currentX.current = 0;
    dragging.current = true;
    actionQueued.current = false;
    direction.current = null;
    e.currentTarget.setPointerCapture(e.pointerId);
    if (ref.current) ref.current.style.transition = 'none';
  };

  const handleMove = (e) => {
    if (!dragging.current) return;
    const diff = e.clientX - startX.current;
    if (direction.current === null && Math.abs(diff) > 5) {
      direction.current = diff > 0 ? 'right' : 'left';
    }
    if (direction.current === 'left' && diff > 0) return;
    if (direction.current === 'right' && diff < 0) return;
    currentX.current = diff;
    if (ref.current) ref.current.style.transform = `translateX(${diff}px)`;
  };

  const handleEnd = () => {
    if (!dragging.current) return;
    dragging.current = false;
    if (!ref.current) return;
    ref.current.style.transition = 'transform 0.25s ease';
    if (currentX.current < -threshold && !actionQueued.current) {
      actionQueued.current = true;
      ref.current.style.transform = 'translateX(-100%)';
      setTimeout(onDelete, 250);
    } else if (currentX.current > threshold && !actionQueued.current && onComplete) {
      actionQueued.current = true;
      ref.current.style.transform = 'translateX(0)';
      onComplete();
    } else {
      ref.current.style.transform = 'translateX(0)';
    }
    direction.current = null;
  };

  return (
    <div className="relative overflow-hidden rounded-lg">
      <div className="absolute inset-0 flex items-center justify-end pr-6 bg-red-500/15 rounded-lg">
        <Trash2 className="h-5 w-5 text-red-500" />
      </div>
      <div className={`absolute inset-0 flex items-center justify-start pl-6 rounded-lg ${isCompleted ? 'bg-amber-500/15' : 'bg-green-500/15'}`}>
        {isCompleted ? <X className="h-5 w-5 text-amber-400" /> : <Check className="h-5 w-5 text-green-500" />}
      </div>
      <div
        ref={ref}
        onPointerDown={handleStart}
        onPointerMove={handleMove}
        onPointerUp={handleEnd}
        onPointerCancel={handleEnd}
        className="relative bg-background z-10"
        style={{ touchAction: 'pan-y' }}
      >
        {children}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   <SetRow>  — memoized leaf

   The performance gate. Wrapped in `memo(..., arePropsEqual)`
   so a keystroke into one set's weight/reps input only re-renders
   that ONE SetRow — sibling rows skip render because every prop
   is reference-equal.

   The custom comparator below checks all 16 standard props from
   the spec PLUS the bundled `setActions` object and the per-row
   `isContextMenuOpen` flag. Provider-side `updateSet` produces a
   new `set` object at the changed index only; sibling sets retain
   referential equality. Action callbacks are useCallback-stable
   in ExerciseRow with deps consisting only of context-stable refs.
   ════════════════════════════════════════════════════════════════ */
function SetRowImpl({
  exIdx,
  setIdx,
  set,
  isCompleted,
  isIncomplete,
  previousSet,
  exerciseEquipment, // eslint-disable-line no-unused-vars
  appliedDirection,
  prefs,
  inputMode,
  cardioFields = [],
  setLabel,
  isContextMenuOpen,
  onUpdateSet,
  onCompleteSet,
  onUncompleteSet, // eslint-disable-line no-unused-vars
  onDeleteSet,
  onLongPressStart,
  onLongPressMove,
  onLongPressEnd,
  onContextMenuOpen,
  onContextMenuClose,
  setActions,
}) {
  const { t } = useTranslation();
  const setType = SET_TYPES[set.set_type] || SET_TYPES.work;

  // Three layouts:
  //   reps   : Set | Prev | KG     | REPS    | (RPE) | ✓
  //   time   : Set | Prev | mm:ss             | (RPE) | ✓
  //   cardio : Set | Prev | min    | …metrics…        | (RPE) | ✓
  const isTime = inputMode === 'time';
  const isCardio = inputMode === 'cardio';
  const nMetrics = isCardio ? cardioFields.length : 0;

  const gridCols = isCardio
    ? cardioGridClass(nMetrics, prefs.rpeTrackingEnabled)
    : isTime
    ? (prefs.rpeTrackingEnabled ? 'grid-cols-[44px_60px_1fr_48px_36px]' : 'grid-cols-[44px_60px_1fr_36px]')
    : (prefs.rpeTrackingEnabled ? 'grid-cols-[44px_60px_1fr_1fr_48px_36px]' : 'grid-cols-[44px_60px_1fr_1fr_36px]');

  return (
    <SwipeableSet
      onDelete={() => onDeleteSet(exIdx, setIdx)}
      onComplete={() => onCompleteSet(exIdx, setIdx)}
      isCompleted={isCompleted}
    >
      <div
        className={`grid ${gridCols} gap-2 items-center px-1 py-2 rounded-lg transition-colors ${
          isCompleted ? 'bg-green-500/8' : isIncomplete ? 'bg-red-500/10 ring-1 ring-red-500/50' : ''
        }`}
        data-testid={`set-${exIdx}-${setIdx}`}
        data-tour-anchor={exIdx === 0 && setIdx === 0 ? TA.WORKOUT_FIRST_SET_ROW : undefined}
        onPointerDown={(e) => onLongPressStart(e, exIdx, setIdx)}
        onPointerMove={onLongPressMove}
        onPointerUp={onLongPressEnd}
        onPointerLeave={onLongPressEnd}
        onPointerCancel={onLongPressEnd}
        onContextMenu={(e) => {
          if (e.target?.closest?.('input, button')) return;
          e.preventDefault();
          if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur();
          }
          onContextMenuOpen(exIdx, setIdx);
        }}
        style={{ WebkitTouchCallout: 'none' }}
      >
        {/* Set number — opens type popover */}
        <SetTypePopover
          currentType={set.set_type}
          onSelect={(type) => setActions.onChangeSetType(exIdx, setIdx, type)}
          onDelete={() => onDeleteSet(exIdx, setIdx)}
        >
          <button
            className={`w-9 h-9 rounded-full flex items-center justify-center font-bold transition-colors cursor-pointer ${
              setLabel.length > 1 ? 'text-[10px]' : 'text-xs'
            } ${
              isCompleted
                ? 'bg-green-500 text-white'
                : `${setType.color} text-white`
            }`}
            data-testid={`set-number-btn-${exIdx}-${setIdx}`}
          >
            {setLabel}
          </button>
        </SetTypePopover>

        {/* Previous column — adapts to mode:
              reps   → "kg×reps"
              time   → "mm:ss"
              cardio → "Nm …metrics…" (compact: minutes + each metric) */}
        <div className="text-center text-xs text-muted-foreground font-mono" data-testid={`previous-${exIdx}-${setIdx}`}>
          {isCardio
            ? (() => {
                const ps = previousSet || {};
                const mins = Math.round((parseInt(ps.duration_seconds, 10) || 0) / 60);
                const bits = [];
                if (mins) bits.push(`${mins}m`);
                for (const f of cardioFields) {
                  const raw = ps[f.key];
                  if (!raw) continue;
                  if (f.key === 'distance_m') bits.push(f.unit === 'km' ? `${Math.round(raw) / 1000}k` : `${raw}m`);
                  else if (f.key === 'resistance_level') bits.push(`l${raw}`);
                  else if (f.key === 'speed_kmh') bits.push(`${raw}`);
                  else if (f.key === 'incline_pct') bits.push(`@${raw}%`);
                }
                return bits.length ? bits.join(' ') : '-';
              })()
            : isTime
            ? (previousSet?.duration_seconds > 0 ? formatDuration(previousSet.duration_seconds) : '-')
            : (previousSet && (previousSet.weight > 0 || previousSet.reps > 0)
              ? `${previousSet.weight}×${previousSet.reps || 0}`
              : '-')
          }
        </div>
        {isCardio ? (
          // CARDIO input: minutes (no seconds — cardio is logged to the minute) plus
          // one column per descriptor from getCardioFields (distance / speed / incline /
          // resistance level — varies by machine profile).
          <>
            <Input
              type="number"
              inputMode="numeric"
              min="0"
              value={(() => {
                const total = parseInt(set.duration_seconds, 10);
                if (total === undefined || total === null || isNaN(total)) return '';
                return Math.floor(total / 60);
              })()}
              onChange={(e) => setActions.onUpdateSetDuration(exIdx, setIdx, 'minutes', e.target.value)}
              className={`h-11 md:h-9 text-center font-mono text-base md:text-sm ${isCompleted ? 'border-green-500/30 text-green-700 dark:text-green-400' : ''}`}
              placeholder={(() => {
                const prev = parseInt(previousSet?.duration_seconds, 10) || 0;
                return prev ? String(Math.round(prev / 60)) : 'min';
              })()}
              data-testid={`duration-min-input-${exIdx}-${setIdx}`}
              aria-label={t('workout.colMinutes')}
            />
            {cardioFields.map((f) => (
              <Input
                key={f.key}
                type="number"
                inputMode={f.htmlInputMode}
                step={f.step}
                min="0"
                value={f.toDisplay(set[f.key])}
                onChange={(e) => setActions.onUpdateSetCardioField(exIdx, setIdx, f.key, f.fromInput(e.target.value))}
                className={`h-11 md:h-9 text-center font-mono text-base md:text-sm ${isCompleted ? 'border-green-500/30 text-green-700 dark:text-green-400' : ''}`}
                placeholder={previousSet?.[f.key] ? String(f.toDisplay(previousSet[f.key])) : f.placeholder}
                data-testid={`cardio-${f.key}-input-${exIdx}-${setIdx}`}
                aria-label={t(f.headerKey)}
              />
            ))}
          </>
        ) : isTime ? (
          // TIME input: minutes + seconds, replaces KG and REPS columns
          (() => {
            const total = parseInt(set.duration_seconds, 10) || 0;
            const mm = Math.floor(total / 60);
            const ss = total % 60;
            const prevTotal = parseInt(previousSet?.duration_seconds, 10) || 0;
            const prevMm = prevTotal ? Math.floor(prevTotal / 60) : '';
            const prevSs = prevTotal ? String(prevTotal % 60).padStart(2, '0') : '';
            // Show empty string only when duration_seconds is truly not set (user hasn't entered anything yet).
            // If duration_seconds exists (even if 0), show the actual values (including 0) so users can log 00:30, 00:56, etc.
            const showMm = set.duration_seconds !== undefined && set.duration_seconds !== null && set.duration_seconds !== '' ? mm : '';
            const showSs = set.duration_seconds !== undefined && set.duration_seconds !== null && set.duration_seconds !== '' ? ss : '';
            return (
              <div className="flex items-center gap-1 justify-center">
                <Input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  value={showMm}
                  onChange={(e) => setActions.onUpdateSetDuration(exIdx, setIdx, 'minutes', e.target.value)}
                  className={`h-11 md:h-9 w-full text-center font-mono text-base md:text-sm ${isCompleted ? 'border-green-500/30 text-green-700 dark:text-green-400' : ''}`}
                  placeholder={prevMm !== '' ? String(prevMm) : 'mm'}
                  data-testid={`duration-min-input-${exIdx}-${setIdx}`}
                  aria-label={t('workout.colTime') + ' minutes'}
                />
                <span className="text-xs text-muted-foreground font-mono">:</span>
                <Input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  max="59"
                  value={showSs}
                  onChange={(e) => setActions.onUpdateSetDuration(exIdx, setIdx, 'seconds', e.target.value)}
                  className={`h-11 md:h-9 w-full text-center font-mono text-base md:text-sm ${isCompleted ? 'border-green-500/30 text-green-700 dark:text-green-400' : ''}`}
                  placeholder={prevSs || 'ss'}
                  data-testid={`duration-sec-input-${exIdx}-${setIdx}`}
                  aria-label={t('workout.colTime') + ' seconds'}
                />
              </div>
            );
          })()
        ) : (
          <>
            <div className="flex items-center gap-1 flex-1">
              <div className="relative flex-1 min-w-0">
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.5"
                  value={set.weight || ''}
                  onChange={(e) => onUpdateSet(exIdx, setIdx, 'weight', e.target.value)}
                  onKeyDown={(e) => {
                    // Enter on KG → focus REPS (saves a tap per set, matches the
                    // Hevy/Strong logging cadence). Doesn't fire on every keystroke
                    // — user keeps full control over when to advance.
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const reps = document.querySelector(`[data-testid="reps-input-${exIdx}-${setIdx}"]`);
                      if (reps) { reps.focus(); reps.select?.(); }
                    }
                  }}
                  className={`h-11 md:h-9 text-center font-mono text-base md:text-sm w-full
                    ${isCompleted ? 'border-green-500/30 text-green-700 dark:text-green-400' : ''}
                    ${set.set_type === 'work' && appliedDirection ? (appliedDirection === 'increase' ? 'border-green-500 ring-1 ring-green-500/30' : 'border-red-500 ring-1 ring-red-500/30') : ''}
                    `}
                  placeholder={previousSet?.weight ? `${previousSet.weight}` : '0'}
                  data-testid={`weight-input-${exIdx}-${setIdx}`}
                />
                {/* Applied progression marker — tiny corner overlay so it doesn't steal column width */}
                {set.set_type === 'work' && appliedDirection && (
                  <div className="absolute top-0.5 left-0.5 pointer-events-none">
                    {appliedDirection === 'increase'
                      ? <TrendingUp className="h-2.5 w-2.5 text-green-500" data-testid={`auto-progress-up-${exIdx}-${setIdx}`} />
                      : <TrendingDown className="h-2.5 w-2.5 text-red-500" data-testid={`auto-progress-down-${exIdx}-${setIdx}`} />
                    }
                  </div>
                )}
              </div>
            </div>

            {/* Reps input */}
            <Input
              type="number"
              inputMode="numeric"
              value={set.completed_reps || ''}
              onChange={(e) => onUpdateSet(exIdx, setIdx, 'completed_reps', e.target.value)}
              onKeyDown={(e) => {
                // Enter on REPS → blur (close the soft keyboard) and focus the
                // set-complete checkmark so a return-thumb press lands the set.
                // The check button has data-testid="complete-set-{exIdx}-{setIdx}".
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.target.blur();
                  const check = document.querySelector(`[data-testid="complete-set-${exIdx}-${setIdx}"]`);
                  if (check) check.focus();
                }
              }}
              className={`h-11 md:h-9 text-center font-mono text-base md:text-sm ${isCompleted ? 'border-green-500/30 text-green-700 dark:text-green-400' : ''}`}
              placeholder={set.target_reps_min ? `${set.target_reps_min}-${set.target_reps_max}` : `${prefs.defaultRepsMin}-${prefs.defaultRepsMax}`}
              data-testid={`reps-input-${exIdx}-${setIdx}`}
            />
          </>
        )}

        {/* RPE input (conditional) */}
        {prefs.rpeTrackingEnabled && (
          <select
            value={set.rpe || ''}
            onChange={(e) => setActions.onUpdateSetRPE(exIdx, setIdx, e.target.value)}
            className={`h-9 w-full rounded-md border text-center text-xs font-mono bg-background focus:outline-none focus:ring-2 focus:ring-primary/40 appearance-none cursor-pointer ${
              isCompleted ? 'border-green-500/30 text-green-700 dark:text-green-400' : 'border-input'
            } ${set.rpe >= 9 ? 'text-red-500 font-bold' : set.rpe >= 7 ? 'text-yellow-600 dark:text-yellow-400' : ''}`}
            data-testid={`rpe-input-${exIdx}-${setIdx}`}
          >
            <option value="">-</option>
            <option value="6">6</option>
            <option value="6.5">6.5</option>
            <option value="7">7</option>
            <option value="7.5">7.5</option>
            <option value="8">8</option>
            <option value="8.5">8.5</option>
            <option value="9">9</option>
            <option value="9.5">9.5</option>
            <option value="10">10</option>
          </select>
        )}

        {/* Checkmark to complete */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => onCompleteSet(exIdx, setIdx)}
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                isCompleted
                  ? 'bg-green-500 text-white'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
              data-testid={`complete-set-${exIdx}-${setIdx}`}
            >
              <Check className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>{t('tooltips.completeSet')}</TooltipContent>
        </Tooltip>
      </div>
      <Popover
        open={isContextMenuOpen}
        onOpenChange={(open) => { if (open) onContextMenuOpen(exIdx, setIdx); else onContextMenuClose(); }}
      >
        <PopoverTrigger asChild>
          <span className="sr-only" aria-hidden />
        </PopoverTrigger>
        <PopoverContent className="w-56 p-1.5" align="center" side="bottom">
          <p className="px-2.5 py-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {t('workout.setCtxTitle', { label: setLabel })}
          </p>
          {(() => {
            const close = onContextMenuClose;
            const isDone = isCompleted;
            const menuItem = (icon, label, color, onClick) => (
              <button key={label} onClick={() => { close(); onClick(); }}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-muted/60 transition-colors text-left ${color || ''}`}>
                <span className="flex-shrink-0">{icon}</span>
                <span className="text-sm font-medium">{label}</span>
              </button>
            );
            return (
              <div className="space-y-0.5 mt-1">
                {/* Change set type — inline chips row */}
                <p className="px-2.5 pt-1 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                  {t('workout.setCtxChangeType')}
                </p>
                <div className="flex items-center gap-1 px-2 pb-1">
                  {Object.entries(SET_TYPES).map(([key, st]) => {
                    const Icon = st.icon;
                    const active = set.set_type === key;
                    return (
                      <button
                        key={key}
                        onClick={() => { close(); setActions.onChangeSetType(exIdx, setIdx, key); }}
                        className={`flex-1 h-9 rounded-lg flex items-center justify-center transition-colors ${active ? `${st.color} text-white` : 'bg-muted/40 text-muted-foreground hover:bg-muted/60'}`}
                        title={t(st.labelKey)}
                      >
                        <Icon className="h-4 w-4" />
                      </button>
                    );
                  })}
                </div>
                <div className="h-px bg-border/50 my-0.5" />
                {menuItem(
                  isDone ? <X className="h-4 w-4 text-amber-400" /> : <Check className="h-4 w-4 text-green-400" />,
                  isDone ? t('workout.setCtxMarkUndone') : t('workout.setCtxMarkDone'),
                  '',
                  () => setActions.onSetCompletedToggle(exIdx, setIdx, isDone),
                )}
                {menuItem(<Copy className="h-4 w-4 text-blue-400" />, t('workout.setCtxDuplicate'), '', () => setActions.onDuplicateSet(exIdx, setIdx))}
                {menuItem(<Plus className="h-4 w-4 text-cyan-400" />, t('workout.setCtxAddBelow'), '', () => setActions.onAddSetBelow(exIdx, setIdx))}
                <div className="h-px bg-border/50 my-0.5" />
                {menuItem(<Trash2 className="h-4 w-4 text-red-400" />, t('workout.setCtxDelete'), 'text-red-400', () => onDeleteSet(exIdx, setIdx))}
              </div>
            );
          })()}
        </PopoverContent>
      </Popover>
    </SwipeableSet>
  );
}

/* The custom comparator returns `true` (skip render) only when every prop
 * is reference-equal. The `set` prop is the linchpin: WorkoutSessionProvider's
 * updateSet produces a new `sets` array clone but only a new object at the
 * changed index, so sibling sets keep referential equality and skip render. */
const arePropsEqual = (prev, next) => (
  prev.exIdx === next.exIdx &&
  prev.setIdx === next.setIdx &&
  prev.set === next.set &&
  prev.isCompleted === next.isCompleted &&
  prev.isIncomplete === next.isIncomplete &&
  prev.previousSet === next.previousSet &&
  prev.exerciseEquipment === next.exerciseEquipment &&
  prev.appliedDirection === next.appliedDirection &&
  prev.prefs === next.prefs &&
  prev.inputMode === next.inputMode &&
  prev.cardioFields === next.cardioFields &&
  prev.setLabel === next.setLabel &&
  prev.isContextMenuOpen === next.isContextMenuOpen &&
  prev.onUpdateSet === next.onUpdateSet &&
  prev.onCompleteSet === next.onCompleteSet &&
  prev.onUncompleteSet === next.onUncompleteSet &&
  prev.onDeleteSet === next.onDeleteSet &&
  prev.onLongPressStart === next.onLongPressStart &&
  prev.onLongPressMove === next.onLongPressMove &&
  prev.onLongPressEnd === next.onLongPressEnd &&
  prev.onContextMenuOpen === next.onContextMenuOpen &&
  prev.onContextMenuClose === next.onContextMenuClose &&
  prev.setActions === next.setActions
);

const SetRow = memo(SetRowImpl, arePropsEqual);
export default SetRow;
